/**
 * AnimatedNumber — a Text whose numeric value counts up (or down) to the
 * target with an ease-out curve whenever it changes. Used for dashboard
 * metrics and process counters so live numbers "arrive" instead of popping.
 *
 * rAF-driven state (works on web and native alike); honors reduce-motion by
 * jumping straight to the value. Non-finite values render as-is.
 */
import { useEffect, useRef, useState } from 'react'
import { Text, type GetProps } from 'tamagui'

import { prefersReducedMotion } from './motion'

const DURATION = 900
const easeOutCubic = (p: number) => 1 - Math.pow(1 - p, 3)

export function AnimatedNumber({ value, ...textProps }: { value: number } & GetProps<typeof Text>) {
  const [display, setDisplay] = useState(() => (prefersReducedMotion() ? value : 0))
  // Что сейчас на экране, включая середину счёта: новая цель едет отсюда. Раньше старт
  // запоминался только в конце анимации, и смена значения посреди счёта обратно на прежнее
  // («Состояние»: юрлицо → «Все» быстрее 0,9 с) оставляла промежуточное число (30.10.2026).
  const shownRef = useRef(display)
  const rafRef = useRef(0)

  useEffect(() => {
    if (!Number.isFinite(value) || prefersReducedMotion()) {
      shownRef.current = value
      setDisplay(value)
      return
    }
    const from = shownRef.current
    if (from === value) return
    const start = performance.now()
    const tick = (now: number) => {
      const p = Math.min(1, (now - start) / DURATION)
      const cur = p >= 1 ? value : from + (value - from) * easeOutCubic(p)
      shownRef.current = cur
      setDisplay(cur)
      if (p < 1) rafRef.current = requestAnimationFrame(tick)
    }
    rafRef.current = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(rafRef.current)
  }, [value])

  const rounded = Math.round(display)
  return <Text {...textProps}>{Number.isFinite(rounded) ? String(rounded) : String(value)}</Text>
}
